import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AppLayout } from '../layouts/AppLayout';
import { api } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { BidHealthBadge, HealthIndicator } from '../components/BidHealth';
import { 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  BarChart3,
  PlusCircle,
  ArrowRight,
  Loader2,
  Target,
  Award,
  MessageSquare,
  Zap,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { OnboardingPrompts } from '../components/OnboardingPrompts';
import { OnboardingProgress } from '../components/OnboardingProgress';

function MiniSparkline({ data, color = '#f97316' }) {
  const values = data.filter(v => v !== null);
  if (values.length < 2) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const w = 200;
  const h = 40;
  const pad = 2;

  // Build points from the full array, skipping nulls at start
  const points = [];
  let started = false;
  data.forEach((v, i) => {
    if (v === null && !started) return;
    started = true;
    if (v !== null) {
      const x = pad + (i / (data.length - 1)) * (w - 2 * pad);
      const y = h - pad - ((v - min) / range) * (h - 2 * pad);
      points.push(`${x.toFixed(1)},${y.toFixed(1)}`);
    }
  });

  if (points.length < 2) return null;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-10" preserveAspectRatio="none">
      <polyline
        points={points.join(' ')}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BidHealthTrendCard({ data }) {
  if (!data) return null;

  const { not_enough_data_flag, low_sample_flag, health_30d, delta_pts, sparkline, sample_count_30d } = data;

  // Not enough data state
  if (not_enough_data_flag) {
    return (
      <Card className="card-industrial" data-testid="bid-health-trend-card">
        <CardContent className="pt-6 pb-5">
          <p className="kpi-label mb-2">Bid Health Trend (30 days)</p>
          <p className="text-sm text-slate-500">Not enough bids yet</p>
          <p className="text-xs text-slate-400 mt-1">Add at least 3 submitted bids to see a trend.</p>
        </CardContent>
      </Card>
    );
  }

  const sparkColor = health_30d >= 60 ? '#10b981' : health_30d >= 40 ? '#f97316' : '#ef4444';

  return (
    <Card className="card-industrial" data-testid="bid-health-trend-card">
      <CardContent className="pt-6 pb-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="kpi-label">Bid Health Trend (30 days)</p>
            <p className="text-2xl font-mono font-bold text-slate-900 mt-1" data-testid="health-30d-value">
              {health_30d !== null ? Math.round(health_30d) : '--'}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              Based on {sample_count_30d} bid{sample_count_30d !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="text-right">
            {delta_pts !== null ? (
              <p className={`text-sm font-mono font-medium ${delta_pts >= 0 ? 'text-green-600' : 'text-red-500'}`} data-testid="delta-display">
                {delta_pts >= 0 ? (
                  <><TrendingUp className="w-3.5 h-3.5 inline mr-0.5" />{`+${delta_pts.toFixed(1)}`}</>
                ) : (
                  <><TrendingDown className="w-3.5 h-3.5 inline mr-0.5" />{delta_pts.toFixed(1)}</>
                )}
                <span className="text-slate-400 ml-0.5">pts vs prior 30d</span>
              </p>
            ) : low_sample_flag ? (
              <p className="text-xs text-slate-400" data-testid="low-sample-notice">Low sample — trend may be noisy</p>
            ) : (
              <p className="text-xs text-slate-400" data-testid="delta-na">&Delta; n/a</p>
            )}
          </div>
        </div>

        {sparkline && sparkline.some(v => v !== null) && (
          <MiniSparkline data={sparkline} color={sparkColor} />
        )}
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [portfolioHealth, setPortfolioHealth] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashRes, portfolioRes, trendRes] = await Promise.all([
          api.get('/dashboard'),
          api.get('/analysis/portfolio').catch(() => null),
          api.get('/metrics/bid-health-trend?days=30').catch(() => null),
        ]);
        setData(dashRes.data);
        if (portfolioRes?.data?.portfolio_health) {
          setPortfolioHealth(portfolioRes.data.portfolio_health);
        }
        if (trendRes?.data) {
          setTrendData(trendRes.data);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      </AppLayout>
    );
  }

  const { kpis, worst_bids, margin_distribution, monthly_trend, target_margin, currency } = data || {};
  const currencySymbol = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : currency;

  // Empty state — first-time user: action-first, not exploration
  if (!kpis?.total_bids) {
    return (
      <AppLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-orange-100 rounded-sm mx-auto mb-5 flex items-center justify-center">
              <Zap className="w-8 h-8 text-orange-600" strokeWidth={1.5} />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              Run Your First Bid Check
            </h1>
            <p className="text-sm text-slate-500 mb-6 leading-relaxed">
              Find out if you're underpricing before you submit. Get an instant commercial decision — margin position, downside exposure, and recommended action.
            </p>

            <OnboardingPrompts />

            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
              <Link to="/app/bids/new">
                <Button className="btn-primary" data-testid="empty-new-bid-btn">
                  <PlusCircle className="mr-2 w-5 h-5" />
                  New Bid Check
                </Button>
              </Link>
              <Link to="/app/upload">
                <Button variant="outline" className="btn-secondary" data-testid="empty-upload-btn">
                  Upload Past Bids
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  const getMarginClass = (margin) => {
    if (margin >= target_margin) return 'margin-positive';
    if (margin >= 0) return 'margin-warning';
    return 'margin-negative';
  };

  return (
    <AppLayout>
      <div className="space-y-8" data-testid="dashboard-container">
        {/* Onboarding Prompt */}
        <OnboardingPrompts />

        {/* Header with Portfolio Health */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div>
            <h1 className="page-title">Dashboard</h1>
            <p className="text-slate-500">Your bid portfolio at a glance</p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {/* Portfolio Health Badge */}
            {portfolioHealth && portfolioHealth.score > 0 && (
              <BidHealthBadge
                score={portfolioHealth.score}
                level={portfolioHealth.level}
                color={portfolioHealth.color}
                interpretation={portfolioHealth.interpretation}
                size="medium"
              />
            )}
            
            <Link to="/app/bids/new">
              <Button className="btn-primary" data-testid="dashboard-new-bid-btn">
                <PlusCircle className="mr-2 w-5 h-5" />
                New Bid Check
              </Button>
            </Link>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="card-industrial">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="kpi-label">Average Margin</p>
                  <p className={`kpi-value ${getMarginClass(kpis.avg_margin_pct)}`}>
                    {kpis.avg_margin_pct?.toFixed(1)}%
                  </p>
                </div>
                {kpis.avg_margin_pct >= target_margin ? (
                  <TrendingUp className="w-6 h-6 text-green-500" />
                ) : (
                  <TrendingDown className="w-6 h-6 text-orange-500" />
                )}
              </div>
              <p className="text-xs text-slate-500 mt-2">Target: {target_margin}%</p>
            </CardContent>
          </Card>

          <Card className="card-industrial">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="kpi-label">Below Target</p>
                  <p className="kpi-value text-orange-500">
                    {kpis.below_target_pct?.toFixed(0)}%
                  </p>
                </div>
                <AlertTriangle className="w-6 h-6 text-orange-500" />
              </div>
              <p className="text-xs text-slate-500 mt-2">{kpis.bids_below_target} of {kpis.total_bids} bids</p>
            </CardContent>
          </Card>

          <Card className="card-industrial">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="kpi-label">Win Rate</p>
                  <p className="kpi-value">
                    {kpis.win_rate !== null ? `${kpis.win_rate?.toFixed(0)}%` : '--'}
                  </p>
                </div>
                <Award className="w-6 h-6 text-blue-500" />
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {kpis.win_rate !== null ? 'Based on outcomes' : 'Add outcomes to see'}
              </p>
            </CardContent>
          </Card>

          <Card className="card-industrial">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="kpi-label">Total Bid Value</p>
                  <p className="kpi-value">
                    {currencySymbol}{(kpis.total_bid_value / 1000).toFixed(0)}k
                  </p>
                </div>
                <Target className="w-6 h-6 text-slate-500" />
              </div>
              <p className="text-xs text-slate-500 mt-2">{kpis.total_bids} bids analysed</p>
            </CardContent>
          </Card>
        </div>

        {/* Bid Health Trend Card */}
        <BidHealthTrendCard data={trendData} />

        {/* Onboarding Progress Widget */}
        <OnboardingProgress />

        {/* Charts Row */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Margin Distribution */}
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="section-title text-lg">Margin Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={margin_distribution} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <XAxis 
                      dataKey="range" 
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      axisLine={{ stroke: '#e2e8f0' }}
                    />
                    <YAxis 
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      axisLine={{ stroke: '#e2e8f0' }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        background: '#0f172a', 
                        border: 'none', 
                        borderRadius: '2px',
                        color: '#fff',
                        fontSize: '12px'
                      }}
                    />
                    <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                      {margin_distribution?.map((entry, index) => (
                        <Cell 
                          key={index} 
                          fill={
                            entry.range === '<0%' ? '#ef4444' :
                            entry.range === '0-5%' ? '#f97316' :
                            entry.range === '5-10%' ? '#eab308' :
                            '#10b981'
                          } 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Trend */}
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="section-title text-lg">Monthly Margin Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                {monthly_trend?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthly_trend} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                      <XAxis 
                        dataKey="month" 
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        axisLine={{ stroke: '#e2e8f0' }}
                      />
                      <YAxis 
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        axisLine={{ stroke: '#e2e8f0' }}
                        domain={[0, 'auto']}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          background: '#0f172a', 
                          border: 'none', 
                          borderRadius: '2px',
                          color: '#fff',
                          fontSize: '12px'
                        }}
                        formatter={(value) => [`${value}%`, 'Avg Margin']}
                      />
                      <Bar dataKey="avg_margin" fill="#f97316" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-500">
                    Not enough data for trend analysis
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Worst Performing Bids */}
        <Card className="card-industrial">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="section-title text-lg">Bids Needing Attention</CardTitle>
            <Link to="/app/bids">
              <Button variant="ghost" size="sm">
                View All
                <ArrowRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Ref</th>
                    <th>Client</th>
                    <th>Price</th>
                    <th>Margin</th>
                    <th>Risk Spread</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {worst_bids?.slice(0, 5).map((bid) => (
                    <tr key={bid.id}>
                      <td>
                        <Link to={`/app/bids/${bid.id}`} className="font-medium text-orange-600 hover:underline">
                          {bid.bid_ref}
                        </Link>
                      </td>
                      <td className="text-slate-600">{bid.client_name}</td>
                      <td className="font-mono">{currencySymbol}{bid.submitted_price?.toLocaleString()}</td>
                      <td>
                        <span className={`font-mono font-semibold ${getMarginClass(bid.margin_pct)}`}>
                          {bid.margin_pct?.toFixed(1)}%
                        </span>
                      </td>
                      <td className="font-mono">{bid.risk_spread?.toFixed(1)}%</td>
                      <td>
                        <Badge variant={
                          bid.outcome === 'won' ? 'default' :
                          bid.outcome === 'lost' ? 'secondary' :
                          'outline'
                        }>
                          {bid.outcome || bid.status}
                        </Badge>
                      </td>
                      <td>
                        <Link to={`/app/bids/${bid.id}`} data-testid={`ask-btn-${bid.id}`} title="Ask about this analysis">
                          <MessageSquare className="w-3.5 h-3.5 text-purple-400 hover:text-purple-300 cursor-pointer" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
